import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Cpu, Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

export default function Purchases() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [purchases, setPurchases] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => { api.get("/shop/purchases").then((r) => setPurchases(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const download = async (ent) => {
    setBusy(ent.id);
    try {
      const { data } = await api.post(`/shop/purchases/${ent.id}/download-link`);
      const a = document.createElement("a");
      // The server returns an absolute API path, already including /api.
      const backend = new URL(API, window.location.origin);
      const downloadUrl = new URL(data.url, backend.origin);
      if (downloadUrl.origin !== backend.origin || !downloadUrl.pathname.startsWith("/api/shop/dl/")) {
        throw new Error(de ? "Ungültiger Download-Link" : "Invalid download link");
      }
      a.href = downloadUrl.href; a.download = data.filename || "download";
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(de ? "Download gestartet" : "Download started");
      setTimeout(load, 1000);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-black">{de ? "Meine Käufe" : "My Purchases"}</h1>

      <Link to="/portal/withdrawals" className="btn-ghost inline-flex">{de ? "Vertrag widerrufen / Widerrufe ansehen" : "Withdraw from contract / View withdrawals"}</Link>
      {purchases.length === 0 ? (
        <div className="surface p-12 text-center" data-testid="purchases-empty">
          <ShoppingBag size={44} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">{de ? "Noch keine Käufe." : "No purchases yet."}</p>
          <Link to="/shop" className="btn-primary mt-5 inline-flex !py-2 !px-4 text-sm">{de ? "Zum Shop" : "Go to shop"}</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="purchases-downloads">
          {purchases.map((e) => {
            const Icon = e.product_type === "software" ? Cpu : Download;
            return (
              <div key={e.id} className="surface p-5" data-testid={`purchase-${e.id}`}>
                <div className="flex items-start gap-4">
                  <div className="h-14 w-14 rounded-md bg-secondary overflow-hidden shrink-0 flex items-center justify-center">
                    {(e.images || [])[0] ? <img src={e.images[0]} alt="" className="h-full w-full object-cover" /> : <Icon size={22} className="text-primary" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{e.product_name}</div>
                    {e.current_version && <div className="text-xs text-muted-foreground">{de ? "Version" : "Version"} {e.current_version}</div>}
                    {e.download_limit && <div className="text-xs text-muted-foreground">{e.download_count || 0}/{e.download_limit} {de ? "Downloads" : "downloads"}</div>}
                  </div>
                </div>
                {e.order_id && <Link to={`/portal/withdrawals?source=shop&id=${encodeURIComponent(e.order_id)}`} className="block text-sm text-primary underline mt-4">{de ? "Vertrag widerrufen" : "Withdraw from contract"}</Link>}
                {e.has_file ? (
                  <button onClick={() => download(e)} disabled={busy === e.id} className="btn-primary w-full mt-4 !py-2 text-sm" data-testid={`purchase-download-${e.id}`}>
                    {busy === e.id ? <Loader2 size={15} className="animate-spin" /> : <><Download size={15} /> {e.current_filename || (de ? "Herunterladen" : "Download")}</>}
                  </button>
                ) : <p className="text-xs text-muted-foreground mt-4">{de ? "Kein Download verfügbar." : "No download available."}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

